import React, { useContext, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import Auth from '@cpg-its/ads.auth.auth';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import SessionTimeout from '@cpg-its/ads.auth.session-timeout';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { LocalStorageKeys } from '../constants/local-storage';
import { AuthenticatedState, AuthToken } from '../models/authenticated-model';
import { dispatchSetApplicationList, setAccessToken, setBenefitFacts, setClientGuid, setRoleObject, setRoles } from '../ducks/authenticated-slice';
import { TcsmStates } from '../../root/tcsm-model';
import { ProfileKey } from '../../pages/arc/constants/constant-strings';
import { getApplications, getRoles } from '../ducks/wam-api';
import { AuthSetting } from '../constants/auth-settings';
import { getPersonBenefitFacts } from '../ducks/mlps-api';
import { setCookie } from '../helpers/auth-helpers';
import { beginLogout, isLoggingOut } from '../helpers/logout-state';
import { establishSiteSession } from '../helpers/site-session-bridge';
import { registerOdpLogin, resetOdpOnLogout } from '../helpers/odp-helper';
import { YesNoIndicators } from '../models/api-model';

interface AuthContentProps {
	redirectUri: string;
}

const onlyUnique = (value, index, self) => {
	return self.indexOf(value) === index;
};

const parseRoleNames = (data: any) => {
	if (!data || !data?.role_assignments) {
		return [];
	}
	const keys = Object.keys(data?.role_assignments);
	return Array.prototype.concat.apply([], keys?.map(key => data?.role_assignments[key].map(a => a?.role_name)));
};

const cookieTime = 0.04;
function AuthContent(props: React.PropsWithChildren<AuthContentProps>) {

	const { children, redirectUri } = props;
	const { error, isLoading, isAuthenticated, loginWithRedirect, logout, getAccessToken, accessToken, decodedToken } = useContext(AuthContext) as any;
	const authenticatedState = useSelector<TcsmStates, AuthenticatedState>(state => state?.authenticatedState);

	const dispatch = useDispatch();

	const parsedGuid = (() => {
		if (decodedToken && decodedToken[ProfileKey]) {
			return (JSON.parse(decodedToken[ProfileKey]) as AuthToken)?.person?.guid;
		}
		return decodedToken;
	})();

	// store token in redux state whenever it changes
	useEffect(() => {
		if (accessToken) {
			dispatch(setAccessToken(accessToken));

			// TEMPORARY: prime the back-end site-user session from TCSM.
			// The auth bridge normally fires from the global nav (cic-nav), but cic-nav
			// can't reliably obtain a token locally/on the canonical Auth0 domain (silent
			// auth blocked by third-party cookies). TCSM authenticates via full-page
			// redirect, so it can establish the cookie here. The cookie is origin-scoped,
			// so it carries over to server-rendered content pages (e.g. the Benefits
			// Snapshot). Remove once cic-nav can establish the session itself (custom Auth0
			// domain or cacheLocation: 'localstorage'). Requires visiting TCSM once.
			// Personalized content fetches wait on this via whenSiteSessionReady().
			establishSiteSession(accessToken);
		}

		if (!authenticatedState?.clientGuid && parsedGuid) {
			// Shares the global nav's session marker, so a user who lands on a content page and
			// then enters TCSM reports one login, not two.
			registerOdpLogin(parsedGuid);

			dispatch(setClientGuid(parsedGuid));
			dispatch(getPersonBenefitFacts(parsedGuid, (response) => {
				dispatch(setBenefitFacts(response?.data));
				Object.keys(response?.data)?.forEach(vg => {
					document.cookie = `${vg}=${response?.data[vg]}; path=/`;
				});
			}));

			dispatch(getRoles(
				parsedGuid,
				(res) => {
					const roles = parseRoleNames(res?.data)?.filter(onlyUnique);
					sessionStorage?.setItem('rolesMatrix', JSON.stringify(roles));
					if (roles?.includes('CIC_User')) 
						setCookie('CICUSER', YesNoIndicators.Yes, cookieTime);
					else
						setCookie('CICUSER', YesNoIndicators.No, cookieTime);
					dispatch(setRoles(roles));
					dispatch(setRoleObject(res?.data?.role_assignments || {}));
				},
			));

			dispatch(getApplications((res) => dispatchSetApplicationList(res, dispatch)));

			if (!sessionStorage?.getItem('clientGuid') !== parsedGuid) {
				sessionStorage?.setItem('clientGuid', parsedGuid);

			}
		}
	}, [accessToken, decodedToken, authenticatedState?.clientGuid]);

	if (error) {
		return (
			<div>{error.message}</div>
		);
	}

	// must check isLoading before check isAuthenticated
	if (isLoading) {
		return (<LoadingCircle />);
	}

	// store original requested route in local storage before redirecting to login
	if (!isAuthenticated) {
		// ...unless we're signing out. logout() flips isAuthenticated to false before the hosted
		// logout navigation commits; redirecting to login here would cancel it and silently
		// re-authenticate against the still-live SSO session.
		if (isLoggingOut()) {
			return (<LoadingCircle />);
		}

		localStorage.setItem(LocalStorageKeys.AuthRedirect, window.location.pathname + window.location.search);
		loginWithRedirect();
	}

	if (!isAuthenticated || !authenticatedState.accessToken) {
		return <LoadingCircle />;
	}

	// once authenticated, route to the original requested route, then remove it from local storage
	const authRedirect = localStorage.getItem(LocalStorageKeys.AuthRedirect);
	if (authRedirect) {
		localStorage.removeItem(LocalStorageKeys.AuthRedirect);
		return <Redirect to={authRedirect} />;
	}

	const encodedReturnTo = encodeURIComponent(`${AuthSetting.REACT_APP_AUTH0_LOGOUT_URL}?returnTo=${redirectUri}&client_id=${AuthSetting.REACT_APP_CLIENT_ID}`);
	const logoutUrl = `${AuthSetting.REACT_APP_CPG_LOGOUT_URL}?returnTo=${encodedReturnTo}`;

	return (
		<>
			<SessionTimeout
				onActivity={() => getAccessToken()}
				logout={async () => {
					// On idle timeout: clear the server-side bridge session and the auth0-spa-js
					// token cache (now in localStorage) WITHOUT a redirect, so the persisted token
					// can't re-hydrate the session; then proceed to the hosted logout ourselves.
					// (When a `logout` prop is set, SessionTimeout calls it INSTEAD of navigating
					// to logoutUrl, so this callback owns the redirect.)
					// Flag FIRST and synchronously, so the gate above stops forcing a re-login.
					beginLogout();
					// Reset ODP: forget the reported login and clear the VUID cookie.
					resetOdpOnLogout();
					try { await fetch('/api/auth/site-session', { method: 'DELETE', credentials: 'same-origin' }); } catch { /* non-fatal */ }
					try { await logout({ openUrl: false }); } catch { /* non-fatal */ }
					window.location.replace(logoutUrl);
				}}
				logoutUrl={logoutUrl}
				timeout={AuthSetting.REACT_APP_SESSION_TIMEOUT}
				countdown={AuthSetting.REACT_APP_SESSION_COUNTDOWN}
				throttle={AuthSetting.REACT_APP_SESSION_THROTTLE}
			/>
			{children}
		</>
	);
}

interface CustomAuthProps {
	redirectUri: string;
}
export function CustomAuth(props: React.PropsWithChildren<CustomAuthProps>) {
	const { redirectUri, children } = props;

	return (
		<Auth
			audience={AuthSetting.REACT_APP_AUDIENCE}
			cacheLocation="localstorage"
			clientId={AuthSetting.REACT_APP_CLIENT_ID}
			connection={AuthSetting.REACT_APP_CONNECTION}
			domain={AuthSetting.REACT_APP_DOMAIN}
			redirectUri={redirectUri}>
			<AuthContent redirectUri={redirectUri}>
				{children}
			</AuthContent>
		</Auth>
	);
}